import React, { useState, useEffect } from 'react';
import { useNavigation } from 'context/NavigationContext';
import {
  BringzzPageContainer,
  BringzzCountryDropdown,
  BringzzButton,
  BringzzIcon,
  BringzzText,
  BringzzInput
} from '@bringzz/components';
import useUser from 'hooks/useUser';
import useRequest from 'hooks/useRequest';

const EditPharmacyAddress = () => {
  const { backPress, navigate } = useNavigation();
  const { sendRequest, loading } = useRequest();
  const { getUser } = useUser();
  const user = getUser();
  const [pharmacyAddressStreetName, setPharmacyAddressStreetName] = useState('');
  const [pharmacyAddressHouseNumber, setPharmacyAddressHouseNumber] = useState('');
  const [pharmacyAddressEntrance, setPharmacyAddressEntrance] = useState('');
  const [pharmacyAddressFloor, setPharmacyAddressFloor] = useState('');
  const [pharmacyAddressDoor, setPharmacyAddressDoor] = useState('');
  const [pharmacyAddressPostalCode, setPharmacyAddressPostalCode] = useState('');
  const [pharmacyAddressCity, setPharmacyAddressCity] = useState('');
  const [pharmacyAddressCountry, setPharmacyAddressCountry] = useState('');
  const [integrationId, setIntegrationId] = useState('');
  const [isOwner, setIsOwner] = useState(false);

  useEffect(() => {
    getAddress();
  }, []);

  const getAddress = async () => {
    try {
      const response = await sendRequest({
        method: 'GET',
        url: '/module/address/getAddress',
        
      });

      if (response.status === 'success') {
        const address = response.data[0];
        setPharmacyAddressStreetName(address.streetName);
        setPharmacyAddressHouseNumber(address.houseNumber);
        setPharmacyAddressEntrance(address.entrance);
        setPharmacyAddressFloor(address.floor);
        setPharmacyAddressDoor(address.door);
        setPharmacyAddressPostalCode(address.postalCode);
        setPharmacyAddressCity(address.cityName);
        setPharmacyAddressCountry(address.countryName);
        setIntegrationId(address.integrationId);
        setIsOwner(address.isOwner);
      }
    } catch (error) {
      console.error('Error fetching address:', error);
    }
  };

  const updateAddress = async () => {
    try {
      const response = await sendRequest({
        method: 'PUT',
        url: '/module/address/updateAddress',
        data: {
          integrationId: integrationId,
          addressData: {
            streetName: pharmacyAddressStreetName,
            houseNumber: pharmacyAddressHouseNumber,
            cityName: pharmacyAddressCity,
            postalCode: pharmacyAddressPostalCode,
            countryName: pharmacyAddressCountry,
          },
        },
      });

      if (response.status === 'success') {
        navigate('/settings/pharmacy/info', false);
      }
    } catch (error) {
      console.error('Error updating address:', error);
    }
  };

  return (
    <BringzzPageContainer
      header={{
        left: (
          <BringzzIcon
            size='22'
            icon='IconChevronLeft'
            folder='LineIcons'
            className='cursor-pointer'
            onClick={backPress}
          />
        ),
        title: <BringzzText tag='h2'>Edit address</BringzzText>
      }}
      footer={
        isOwner && (
          <div className='pb-28 px-6'>
            <BringzzButton
              size='lg'
              className='bg-magic-lilac w-full flex justify-center text-midnight-blue'
              onClick={updateAddress}
              disabled={
                loading ||
                (!pharmacyAddressStreetName &&
                  !pharmacyAddressHouseNumber &&
                  !pharmacyAddressPostalCode &&
                  !pharmacyAddressCity &&
                  !pharmacyAddressCountry &&
                  !pharmacyAddressEntrance &&
                  !pharmacyAddressFloor &&
                  !pharmacyAddressDoor)
              }
            >
              Save
            </BringzzButton>
          </div>
        )
      }
    >
      <div className='p-6 space-y-4'>
        <div className='flex flex-1 gap-3'>
          <BringzzInput
            onChange={isOwner ? (e, isValid, newValue) => setPharmacyAddressStreetName(newValue) : undefined}
            defaultValue={pharmacyAddressStreetName}
            id='streename'
            label='Street Name'
            containerClassname='w-full'
            disabled={!isOwner}
            readOnly={!isOwner}
            style={!isOwner ? { pointerEvents: 'none', cursor: 'not-allowed' } : {}}
          />
          <BringzzInput
            onChange={isOwner ? (e, isValid, newValue) => setPharmacyAddressHouseNumber(newValue) : undefined}
            defaultValue={pharmacyAddressHouseNumber}
            id='housenumber'
            label='House Number'
            containerClassname='w-full'
            disabled={!isOwner}
            readOnly={!isOwner}
            style={!isOwner ? { pointerEvents: 'none', cursor: 'not-allowed' } : {}}
          />
        </div>
        <div className='flex flex-1 gap-3'>
          <BringzzInput
            onChange={isOwner ? (e, isValid, newValue) => setPharmacyAddressEntrance(newValue) : undefined}
            defaultValue={pharmacyAddressEntrance}
            id='entrance'
            label='Entrance (opt.)'
            containerClassname='w-full'
            placeholder='Entrance'
            disabled={!isOwner}
            readOnly={!isOwner}
            style={!isOwner ? { pointerEvents: 'none', cursor: 'not-allowed' } : {}}
          />
          <BringzzInput
            onChange={isOwner ? (e, isValid, newValue) => setPharmacyAddressFloor(newValue) : undefined}
            defaultValue={pharmacyAddressFloor}
            id='floor'
            label='Floor (opt.)'
            type='number'
            containerClassname='w-full'
            placeholder='Floor'
            disabled={!isOwner}
            readOnly={!isOwner}
            style={!isOwner ? { pointerEvents: 'none', cursor: 'not-allowed' } : {}}
          />
          <BringzzInput
			onChange={isOwner ? (e, isValid, newValue) => setPharmacyAddressDoor(newValue) : undefined}
			defaultValue={pharmacyAddressDoor}
			id="door"
			label="Door (opt.)"
			type="text"
			containerClassname="w-full"
			placeholder="Door"
			disabled={!isOwner}
			readOnly={!isOwner}
			style={!isOwner ? { pointerEvents: 'none', cursor: 'not-allowed' } : {}}
			/>
        </div>
        <div className='flex flex-1 gap-3'>
          <BringzzInput
            onChange={isOwner ? (e, isValid, newValue) => setPharmacyAddressPostalCode(newValue) : undefined}
            defaultValue={pharmacyAddressPostalCode}
            id='postalcode'
            label='Postal Code'
            containerClassname='w-full'
            disabled={!isOwner}
            readOnly={!isOwner}
            style={!isOwner ? { pointerEvents: 'none', cursor: 'not-allowed' } : {}}
          />
          <BringzzInput
            onChange={isOwner ? (e, isValid, newValue) => setPharmacyAddressCity(newValue) : undefined}
            defaultValue={pharmacyAddressCity}
            id='city'
            label='City'
            containerClassname='w-full'
            disabled={!isOwner}
            readOnly={!isOwner}
            style={!isOwner ? { pointerEvents: 'none', cursor: 'not-allowed' } : {}}
          />
        </div>

        <div className=''>
          <BringzzText tag='h4' className='font-bold'>
            Country
          </BringzzText>
          <div style={!isOwner ? { pointerEvents: 'none', opacity: 0.5 } : {}}>
            <BringzzCountryDropdown
              className='!rounded-r-none p-2 bg-[#F4F4F4]'
              mode={'flag-name'}
              onCountryChange={result => setPharmacyAddressCountry(result)}
              disabled={!isOwner}
            />
          </div>
        </div>
      </div>
    </BringzzPageContainer>
  );
};

export default EditPharmacyAddress;
